import React, { useMemo, useState, useContext, useEffect, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Menu, Icon, Accordion } from "semantic-ui-react";
import { UserContext } from "../context/UserContext";

import { DataMenuJadmin } from "./DataMenuJ";
import { DataMenuSt } from "./DataMenuSt";
import { DataMenuAdmin } from "./DataMenuAdmin";
import { DataMenuSup } from "./DataMenuSup";
import { DataMenuTec } from "./DataMenuTec";
import { DataMenuTransp } from "./DataMenuTransp";
import { DataMenuBackoffice } from "./DataMenuBackoffice";

const COLORS = {
  primary: "#328AC4",
  primarySoft: "#E6F2FA",
  primaryHover: "#F0F7FC",
  text: "#1E293B",
  textSoft: "#64748B",
  border: "#E2E8F0",
  bg: "#FFFFFF",
  overlay: "rgba(15, 23, 42, 0.35)",
};

const pickMenuByRole = (rol) => {
  if (rol === "DADMIN") return DataMenuBackoffice;
  if (rol === "JADMIN") return DataMenuBackoffice;
  if (rol === "ADMIN-LMST") return DataMenuJadmin;
  if (rol === "TECNICO-LMST") return DataMenuSt;
  if (rol === "ADMIN-HC") return DataMenuAdmin;
  if (rol === "SUPERVISOR-HC") return DataMenuSup;
  if (rol === "TECNICO-HC") return DataMenuTec;
  if (rol === "TRANSPORTE-HC") return DataMenuTransp;
  return [];
};

const toAbs = (path) =>
  `/${String(path || "").replace(/^\/+/, "")}`.replace(/\/+/g, "/");

export default function SideBarSemanticOverlay({
  children,
  navHeight = 70,
  sidebarWidth = 270,
  railWidth = 72,
}) {
  const { users } = useContext(UserContext);
  const menu = useMemo(() => pickMenuByRole(users?.rol), [users?.rol]);

  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const location = useLocation();
  const sidebarScrollRef = useRef(null);

  const navigate = useNavigate();

  const go = (path) => {
    navigate(toAbs(path));
  };

  const hasSub = (it) => Array.isArray(it?.subNav) && it.subNav.length > 0;

  useEffect(() => {
    const current = location.pathname;

    const idx = menu.findIndex((m) =>
      m.subNav?.some((s) => toAbs(s.path) === current)
    );

    if (idx !== -1) setActiveIndex(idx);
  }, [location.pathname, menu]);

  useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  useEffect(() => {
    if (visible && sidebarScrollRef.current) {
      sidebarScrollRef.current.scrollTop = 0;
    }
  }, [visible]);

  const baseRow = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: COLORS.text,
    textAlign: "left",
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    borderRadius: 10,
    padding: "11px 14px",
    transition: "all 0.2s ease",
    fontSize: 14,
    margin: "2px 8px",
    textDecoration: "none",
  };

  const applyHover = (el, on) => {
    if (!el) return;
    el.style.background = on ? COLORS.primaryHover : "transparent";
    el.style.transform = on ? "translateX(2px)" : "translateX(0)";
    el.style.color = on ? COLORS.primary : COLORS.text;
    el.style.textDecoration = "none";
  };

  const applyActive = (styleObj) => ({
    ...styleObj,
    background: COLORS.primarySoft,
    borderLeft: `3px solid ${COLORS.primary}`,
    color: COLORS.primary,
    fontWeight: 700,
    textDecoration: "none",
  });

  const iconStyle = {
    color: COLORS.primary,
    minWidth: 20,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const currentPath = location.pathname;

  return (
    <div
      style={{
        paddingTop: navHeight,
        minHeight: "100vh",
        background: "#ffffff",
      }}
    >
      {/* =====================================================
        RAIL LATERAL PERMANENTE
        Reserva 72px para navegación
    ====================================================== */}
      <div
        style={{
          position: "fixed",
          top: navHeight,
          left: 0,
          bottom: 0,
          width: railWidth,
          background: "#ffffff",
          zIndex: 100,
        }}
      >
        {!visible && (
          <button
            onClick={() => setVisible(true)}
            style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              width: 46,
              height: 46,
              border: `1px solid ${COLORS.border}`,
              background: COLORS.primarySoft,
              borderRadius: 12,
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              boxShadow: "0 8px 20px rgba(50, 138, 196, 0.18)",
              transition: "box-shadow 0.2s ease, background 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 10px 24px rgba(50, 138, 196, 0.26)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                "0 8px 20px rgba(50, 138, 196, 0.18)";
            }}
            aria-label="Menú"
            title="Menú"
          >
            <Icon
              name="sidebar"
              size="large"
              style={{
                margin: 0,
                color: COLORS.primary,
              }}
            />
          </button>
        )}
      </div>

      {/* =====================================================
        CONTENIDO PRINCIPAL
        Empieza SIEMPRE después de los 72px del menú
    ====================================================== */}
      <main
        style={{
          marginLeft: railWidth,
          minWidth: 0,
          minHeight: `calc(100vh - ${navHeight}px)`,
          padding: "16px 16px 16px 0",
        }}
      >
        {children}
      </main>

      {visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 8000,
            pointerEvents: "none",
          }}
        >
          <div
            onClick={() => setVisible(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: COLORS.overlay,
              pointerEvents: "auto",
              backdropFilter: "blur(0px)",
            }}
          />

          <div
            style={{
              position: "absolute",
              top: navHeight,
              left: 0,
              width: sidebarWidth,
              height: `calc(100vh - ${navHeight}px)`,
              background: COLORS.bg,
              pointerEvents: "auto",
              borderRight: `1px solid ${COLORS.border}`,
              boxShadow: "4px 0 24px rgba(15, 23, 42, 0.12)",
              animation: "sidebarSlideIn 0.22s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <style>
              {`
                @keyframes sidebarSlideIn {
                  from {
                    transform: translateX(-100%);
                    opacity: 0.6;
                  }
                  to {
                    transform: translateX(0);
                    opacity: 1;
                  }
                }

                .modern-sidebar-scroll::-webkit-scrollbar {
                  width: 7px;
                }

                .modern-sidebar-scroll::-webkit-scrollbar-thumb {
                  background: #cbd5e1;
                  border-radius: 10px;
                }

                .modern-sidebar-scroll::-webkit-scrollbar-track {
                  background: transparent;
                }

                .modern-sidebar-link,
                .modern-sidebar-link:hover,
                .modern-sidebar-link:focus,
                .modern-sidebar-link:active,
                .modern-sidebar-link:visited {
                  text-decoration: none !important;
                }
              `}
            </style>

            <div
              ref={sidebarScrollRef}
              className="modern-sidebar-scroll"
              style={{
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: 58,
                  padding: "0 14px 0 18px",
                  background: COLORS.bg,
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 800,
                      color: COLORS.text,
                      fontSize: 15,
                      lineHeight: "18px",
                    }}
                  >
                    LogisticMed
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: COLORS.textSoft,
                      marginTop: 1,
                    }}
                  >
                    Menú principal
                  </div>
                </div>

                <button
                  onClick={() => setVisible(false)}
                  style={{
                    width: 34,
                    height: 34,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    background: "#fff",
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                    color: COLORS.textSoft,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = COLORS.primarySoft;
                    e.currentTarget.style.color = COLORS.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                    e.currentTarget.style.color = COLORS.textSoft;
                  }}
                  title="Cerrar"
                >
                  <Icon name="close" style={{ margin: 0 }} />
                </button>
              </div>

              <div style={{ padding: "10px 0 16px" }}>
                <Menu
                  vertical
                  fluid
                  borderless
                  style={{
                    background: "transparent",
                    margin: 0,
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  {menu.map((item, idx) => {
                    const itemAbs = toAbs(item.path);

                    const isItemActive =
                      currentPath === itemAbs ||
                      (item.subNav?.some(
                        (s) => toAbs(s.path) === currentPath
                      ) ??
                        false);

                    if (!hasSub(item)) {
                      return (
                        <Menu.Item
                          as={Link}
                          to={itemAbs}
                          key={idx}
                          className="modern-sidebar-link"
                          onClick={() => setVisible(false)}
                          style={isItemActive ? applyActive(baseRow) : baseRow}
                          onMouseEnter={(e) =>
                            !isItemActive &&
                            applyHover(e.currentTarget, true)
                          }
                          onMouseLeave={(e) =>
                            !isItemActive &&
                            applyHover(e.currentTarget, false)
                          }
                        >
                          <span style={iconStyle}>{item.icon}</span>
                          <span style={{ fontWeight: 700 }}>{item.title}</span>
                        </Menu.Item>
                      );
                    }

                    const isOpen = activeIndex === idx;

                    return (
                      <Accordion
                        key={idx}
                        fluid
                        styled={false}
                        style={{
                          margin: 0,
                          boxShadow: "none",
                          background: "transparent",
                        }}
                      >
                        <Accordion.Title
                          active={isOpen}
                          onClick={() => {
                            if (item.path) {
                              go(item.path);
                            }

                            setActiveIndex((prev) =>
                              prev === idx ? -1 : idx
                            );
                          }}
                          style={
                            isItemActive
                              ? applyActive(baseRow)
                              : { ...baseRow }
                          }
                          onMouseEnter={(e) =>
                            !isItemActive &&
                            applyHover(e.currentTarget, true)
                          }
                          onMouseLeave={(e) =>
                            !isItemActive &&
                            applyHover(e.currentTarget, false)
                          }
                        >
                          <span style={iconStyle}>{item.icon}</span>

                          <span
                            style={{
                              fontWeight: 700,
                              flex: 1,
                            }}
                          >
                            {item.title}
                          </span>

                          <Icon
                            name={isOpen ? "angle up" : "angle down"}
                            style={{
                              margin: 0,
                              color: isItemActive
                                ? COLORS.primary
                                : COLORS.textSoft,
                            }}
                          />
                        </Accordion.Title>

                        <Accordion.Content
                          active={isOpen}
                          style={{
                            padding: 0,
                          }}
                        >
                          <Menu.Menu
                            style={{
                              margin: "2px 0 6px",
                            }}
                          >
                            {item.subNav.map((sub, sIdx) => {
                              const subAbs = toAbs(sub.path);
                              const isSubActive = currentPath === subAbs;

                              const subStyle = {
                                ...baseRow,
                                paddingLeft: 42,
                                fontSize: 13,
                                color: COLORS.textSoft,
                                marginTop: 1,
                                marginBottom: 1,
                              };

                              return (
                                <Menu.Item
                                  as={Link}
                                  to={subAbs}
                                  key={sIdx}
                                  className="modern-sidebar-link"
                                  onClick={() => setVisible(false)}
                                  style={
                                    isSubActive
                                      ? applyActive({
                                        ...subStyle,
                                        color: COLORS.primary,
                                        fontWeight: 700,
                                      })
                                      : subStyle
                                  }
                                  onMouseEnter={(e) =>
                                    !isSubActive &&
                                    applyHover(e.currentTarget, true)
                                  }
                                  onMouseLeave={(e) =>
                                    !isSubActive &&
                                    applyHover(e.currentTarget, false)
                                  }
                                >
                                  <span style={iconStyle}>{sub.icon}</span>
                                  <span style={{ fontWeight: 600 }}>
                                    {sub.title}
                                  </span>
                                </Menu.Item>
                              );
                            })}
                          </Menu.Menu>
                        </Accordion.Content>
                      </Accordion>
                    );
                  })}
                </Menu>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}